import React, { useEffect, useRef, useState } from "react"
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps"
import "tippy.js/dist/tippy.css" // Optional styling
import INDIA_TOPO_JSON from "./india_states.json"
import {
  getStateNameFromEnum,
  idStateMapping,
  StateEnumCoordinates,
} from "./centerDetails.js"
import BodyText from "../text/body-text/body-text.js"
import { graphql, useStaticQuery } from "gatsby"
import * as styles from "./MapMarker.module.css"
import LisCenterBox from "../LisCenterBox/LisCenterBox.js"
import { LazyLoadImage } from "react-lazy-load-image-component"

const MapMarker = () => {
  const data = useStaticQuery(graphql`
    query {
      allSector {
        nodes {
          id
          sectorId
          name
          primaryImage
          whyJoin
          createdOn
          modifiedOn
          order
          showOnHome
          showOnMap
          employersList {
            image
            text
          }
        }
      }
      allInstitute {
        nodes {
          id
          instituteId
          name
          state
          zone
          district
          pincode
          address
          sector
          instituteHeadName
          instituteHeadEmail
          instituteHeadPhone
          isActive
          showOnHome
          createdOn
          modifiedOn
        }
      }
    }
  `)

  const institutesList = data?.allInstitute?.nodes || []

  const sectorMapping = getSectorInstituteMapping(
    data?.allSector?.nodes,
    institutesList
  )

  const [selectedSectorId, setSelectedSectorId] = useState("All")

  const sectorImage = selectedSectorId
    ? selectedSectorId == "All"
      ? Object.values(sectorMapping).find(val => val.name == "Other")?.image
      : sectorMapping[selectedSectorId].image
    : undefined

  const selectedInstitutes =
    selectedSectorId === "All"
      ? institutesList
      : sectorMapping[selectedSectorId].institutes

  const stateWiseInstitutes = selectedInstitutes.reduce((acc, institute) => {
    const state = institute.state
    if (!acc[state]) {
      acc[state] = []
    }
    acc[state].push(institute)
    return acc
  }, {})

  const selectedStates = Object.keys(stateWiseInstitutes)

  const [selectedState, setSelectedState] = useState(undefined)

  const selectedStateInstitutes = selectedState
    ? (stateWiseInstitutes[selectedState] || [])
    : []

  useEffect(() => {
    setSelectedState(undefined)
  }, [selectedSectorId])

  const mapRef = useRef(null) // Ref for map container

  useEffect(() => {
    const handleClickOutside = event => {
      if (mapRef.current && mapRef.current.contains(event.target)) {
        setSelectedState(null)
      }
    }
    document.addEventListener("click", handleClickOutside)
    return () => {
      document.removeEventListener("click", handleClickOutside)
    }
  }, [])

  return (
    <>
      <div className="container mx-auto my-3">
        <div className="flex flex-wrap gap-3 items-center justify-center">
          <div
            className={
              selectedSectorId == "All"
                ? styles.activeCourseTab
                : styles.courseTab
            }
            onClick={() => setSelectedSectorId("All")}
          >
            <BodyText variant="bodyText7">All</BodyText>
          </div>
          {Object.keys(sectorMapping).map((sectorId, index) => {
            if (
              !sectorMapping[sectorId].showOnMap
            ) {
              return null
            }
            return (
              <div
                className={
                  selectedSectorId == sectorId
                    ? styles.activeCourseTab
                    : styles.courseTab
                }
                key={sectorId}
                onClick={() => setSelectedSectorId(sectorId)}
              >
                <BodyText variant="bodyText7">
                  {sectorMapping[sectorId]?.name}
                </BodyText>
              </div>
            )
          })}
        </div>
      </div>
      <div className="mt-10 mx-auto flex gap-3 flex-col md:flex-row">
        <div className="md:w-3/5" ref={mapRef}>
          <BodyText variant="bodyText7" className="font-bold text-center mb-4">Click Map For Contact Information</BodyText>
          <ComposableMap
            projection="geoMercator"
            projectionConfig={{
              center: [82.3629, 22.5937],
              scale: 1350,
            }}
            height={800}
            style={{ width: "100%" }}
            className="md:max-h-[70vh]"
          >
            <Geographies geography={INDIA_TOPO_JSON}>
              {({ geographies }) =>
                geographies.map(geo => {
                  const id = geo.properties.ID
                  const stateEnum = idStateMapping[id]
                  const isIncluded = selectedStates.includes(stateEnum)
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={
                        isIncluded
                          ? selectedState === idStateMapping[geo.properties.ID]
                            ? "#ff9800"
                            : "#edc996"
                          : "#88959e"
                      }
                      stroke="#fff"
                      style={{
                        default: { outline: "none", cursor: "pointer" },
                        hover: {
                          outline: "none",
                          cursor: isIncluded ? "pointer" : undefined,
                        },
                        pressed: { outline: "none" },
                      }}
                      strokeWidth={1.5}
                      onClick={e => {
                        e.stopPropagation()
                        if (isIncluded) {
                          setSelectedState(stateEnum)
                        } else {
                          setSelectedState(undefined)
                        }
                      }}
                      tabIndex={-1}
                    />
                  )
                })
              }
            </Geographies>

            {Object.keys(stateWiseInstitutes).map(stateKey => (
              <Marker
                key={stateKey}
                coordinates={StateEnumCoordinates[stateKey]}
              >
                <g
                  style={{ cursor: "pointer" }}
                  onClick={e => {
                    e.stopPropagation()
                    setSelectedState(stateKey)
                  }}
                >
                  <text
                    x={-20}
                    y={0}
                    fill="#000"
                    fontSize="14px"
                    textAnchor="start"
                    fontWeight="600"
                  >
                    {`${getStateNameFromEnum(stateKey)} (${
                      stateWiseInstitutes[stateKey].length
                    })`}
                  </text>
                </g>
              </Marker>
            ))}
          </ComposableMap>
        </div>
        <div className="md:w-2/5 flex flex-col gap-5">
          <div className="px-4">
            <BodyText variant="bodyText6" className="font-bold text-center mb-2">
              Learnet Institute of Skills (LIS) Excellence Centres
            </BodyText>
            {!selectedState
              ? 
              !!sectorImage && <div className="flex items-center justify-center">
                <LazyLoadImage
                  alt={sectorMapping[selectedSectorId]?.name || "Sector"}
                  className={styles.sectorImg}
                  src={sectorImage} />
              </div>
              : 
              selectedStateInstitutes.map((val, index)=>(
                <div key={val?.name} className={`py-2 ${index !== selectedInstitutes.length -1 ? 'border-b border-[#ddd]' : ''}`}>
                  <LisCenterBox
                    name={val.name}
                    address={val.address}
                    email={val.instituteHeadEmail}
                    phone={val.instituteHeadPhone}
                  />
                </div>
              ))
              }
          </div>
        </div>
      </div>
    </>
  )
}
export default MapMarker

const getSectorInstituteMapping = (sectorsList = [], institutesList = []) => {
  const sectors = sectorsList.map(val => ({
    sectorId: val.sectorId,
    name: val.name,
    image: val.primaryImage,
    showOnHome: val.showOnHome,
    showOnMap: val.showOnMap,
    order: val.order,
  }))
  const sectorMapping = {}
  sectors.forEach(sector => {
    sectorMapping[sector.sectorId] = {
      name: sector.name,
      image: sector.image,
      institutes: [],
      order: sector.order,
      showOnHome: sector.showOnHome,
      showOnMap: sector.showOnMap
    }
  })
  institutesList.forEach(institue => {
    const institueSectors = institue?.sector || []
    let alreadyAddedInOthers = 0
    institueSectors.forEach(sectorId => {
      if (Object.keys(sectorMapping).includes(sectorId)) {
        sectorMapping[sectorId].institutes.push(institue)
      } else if (!alreadyAddedInOthers) {
        sectorMapping["Other"].institutes.push(institue)
      }
    })
  })
  return sectorMapping
}
